import type { PeriodId, TermId } from "@/lib/fees";

export type EnrollmentLocale = "sv" | "en" | "es";

export const ENROLLMENT_TEXT = {
  sv: {
    fillTestData: "Fyll i testdata",
    testDataFilled: "Testdata ifyllt.",
    draftRestored: "Vi återställde din påbörjade ansökan.",
    draftBannerTitle: "Påbörjad ansökan sparad",
    draftBannerBody: (time: string) => `Allt du fyller i sparas automatiskt i den här webbläsaren. Senast sparat ${time}.`,
    draftSaving: "Sparar…",
    draftDiscard: "Rensa och börja om",
    draftDiscardConfirmTitle: "Rensa påbörjad ansökan?",
    draftDiscardConfirmBody: "All information du fyllt i försvinner. Det går inte att ångra.",
    draftDiscarded: "Anmälan rensad.",


    studentSectionTitleSingle: "1. Eleven",
    studentSectionTitleMulti: (n: number) => `Elev ${n}`,
    studentSectionDescription:
      "Vi behöver elevens namn, ID-nummer och födelsedatum för att kunna registrera ansökan.",
    removeStudent: "Ta bort elev",
    firstName: "Förnamn",
    lastName: "Efternamn",
    nationality: "Nationalitet",
    chooseNationality: "Välj nationalitet",
    searchCountry: "Sök land…",
    nationalityHint: "Nationaliteten avgör vilken ID-handling vi frågar efter.",
    swedishPersonalNumberHint:
      "Ange hela personnumret, ÅÅÅÅMMDD-1234. Vi lägger till bindestreck och sekel åt dig. Samordningsnummer fungerar också.",
    studentMissingId: "Vi kompletterar ID-numret senare i inskrivningen.",
    guardianMissingId: "Vi kompletterar ID-numret vid inskrivningen.",
    coordinationNumberOk: "Samordningsnummer godkänt",
    personalNumberValid: "Personnumret är giltigt",
    lastDigitsLater: "",
    birthDateFilledAuto: " – födelsedatum ifyllt automatiskt.",
    studentMissingIdCheckbox: (label: string) => `Saknar ${label.toLowerCase()}`,

    birthYear: "Födelseår",
    birthMonth: "Månad",
    birthDay: "Dag",
    year: "År",
    month: "Månad",
    day: "Dag",
    gender: "Kön",
    chooseGender: "Välj kön",
    sameAddressAsStudent1: "Samma adress som elev 1",
    sameAddressAsStudent1Hint: "Kryssa ur om eleven bor på en annan adress.",
    addressLabel: "Adressuppgifter",
    sameAsStudent1Short: (name: string) => `Samma som ${name}`,
    previousSchool: "Tidigare skola",
    previousSchoolHint: "Skolans namn och ort, till exempel Sofia Distans, Stockholm",
    notes: "Hälsa, allergier och medicin",
    notesHint: "Allergier, medicin, kost eller annat som är bra för oss att känna till",
    otherNotes: "Övriga upplysningar",
    otherNotesHint: "Något annat ni vill att skolan ska veta, till exempel önskemål eller praktiska frågor. Skriv inte hälsouppgifter här.",
    shareHealthInformation: "Jag vill lämna uppgifter om hälsa, allergier eller medicin",

    schoolingSectionTitleSingle: "2. Skolgång",
    schoolingSectionTitleMulti: (n: number) => `Skolgång – elev ${n}`,
    schoolingSectionDescription:
      "Terminen styr både vilken årskurs vi föreslår och vilken avgift som gäller.",
    desiredStart: "Önskad start",
    chooseTerm: "Välj termin",
    termHint: "Höstterminen börjar i september, vårterminen i januari.",
    period: "Period",
    choosePeriod: "Välj period",
    periodHint:
      "Hel termin eller helår är vanligast. Korttid gäller kortare vistelser och har lägre inskrivningsavgift.",
    suggestPrefix: (label: string, term: string) =>
      `Vi föreslår: ${label} (${term}). Du kan ändra nivån nedan om ni önskar en annan placering.`,
    fillBirthDateForSuggestion: "Fyll i födelsedatum ovan så räknar vi ut rätt årskurs och skolform.",
    gradeLevel: "Årskurs / nivå",
    chooseLevel: "Välj nivå",
    fillBirthDateAbove: "Fyll i födelsedatum ovan",
    gradeHint: "Åk 7–9 läses via Sofia Distans och gymnasiet via Hermods, med lärarstöd på plats.",
    shortStartFrom: "Korttid från",
    startDate: "Startdatum",
    termStartsAuto: "Terminen startar automatiskt på första skoldagen.",
    chooseSchoolDayBetween: (first: string, last: string) =>
      `Välj en skoldag mellan ${first} och ${last}. Lov och röda dagar går inte att välja.`,
    schoolConfirmsStartDate: "Skolan bekräftar startdatum för senare läsår.",
    earliestStartNotice: (date: string) => `Tidigast möjliga start är ${date}, 15 dagar efter ansökan.`,
    confirmedBySchool: "Bekräftas av skolan",
    termStartSuffix: " (terminsstart)",
    chooseOtherDate: "Annat datum",
    termStart: "Terminsstart",
    shortEndDate: "Korttid till och med",
    shortEndDateHint: "Slutdatumet räknas ut automatiskt utifrån läsårskalendern.",
    shortEndDatePending: "Välj startdatum först",
    shortWeeksCount: "Antal veckor",
    shortMonthsCount: "Antal månader",
    weeksOption: (n: number) => (n === 1 ? "1 vecka" : `${n} veckor`),
    monthsOption: (n: number) => (n === 1 ? "1 månad" : `${n} månader`),
    shortStayOutsideTerm:
      "Vistelsen ryms inte inom terminen. Välj färre veckor/månader eller ett tidigare startdatum.",
    endDateAfterStart: "Slutdatum måste vara efter startdatum.",
    shortWeekHint: "Korttid per vecka gäller max två veckor.",
    shortMonthHint: "Korttid per månad gäller max två månader.",
    wantsAfterschool: "Vi anmäler oss till fritids",
    wantsAfterschoolHint: "Fritids efter skoldagen för elever i förskoleklass till åk 6.",
    afterschoolDiscountBadge: "Spara 250 €",
    afterschoolDiscountHint: "Spara 250 € om du väljer fritids i samband med ansökan. Gäller termin och helår.",
    afterschoolOffer:
      "Just nu: 250 € rabatt när ni anmäler er till fritids i samband med ansökan. Gäller termin och helår.",
    passportIssuedIn: (country: string) => `Pass utfärdat i ${country}`,
    passportOtherCountry: "Passet är utfärdat i ett annat land",
    wantsFootballAcademy: "Vi anmäler oss till Malmö FF:s fotbollsakademi",
    wantsFootballAcademyHint:
      "Träning enligt Malmö FF:s modell. Kräver skolplats hos oss med distansstudier. 1 000 € per termin – aktiviteten bokas en termin i taget, även om du väljer helår på skolan.",
wantsPadelTennis: "Vi anmäler oss till padel- och tennisprofilen",

    wantsPadelTennisHint:
      "1 000 € per termin. Bokas en termin i taget, även vid helår på skolan.",

    specialNeeds: "Behov av särskilt stöd",
    specialNeedsHint: "Till exempel diagnos, extra anpassningar eller stöd i skolsituationen.",
    specialNeedsNote: "Berätta gärna mer om behovet",

    learnMore: "Läs mer",
    closeModal: "Stäng",
    infoAfterschool: {
      title: "Fritids",
      body: [
        "Fritids är omsorg efter skoldagen för elever från förskoleklass till och med årskurs 6. Barnen får en trygg plats med personal på skolan, tid för lek, läxor och aktiviteter utomhus.",
        "Fritids bokas per termin i ansökan och faktureras tillsammans med skolavgiften. Behöver ni bara enstaka dagar löser vi det i dialog med skolan.",
      ],
    },
    infoFootball: {
      title: "Malmö FF:s fotbollsakademi",
      body: [
        "Fotbollsakademin drivs i samarbete med Malmö FF och följer klubbens träningsmodell. Träningen ligger i anslutning till skoldagen och kombineras med distansstudier via Sofia Distans eller Hermods med lärarstöd på plats.",
        "Akademin är öppen för elever i årskurs 7–8 och kräver en skolplats hos oss. Aktiviteten bokas en termin i taget, utöver skolavgiften.",
        "Vid deltagande i Malmö FF:s fotbollsakademi hjälper skolan till med hela anmälningsprocessen och de villkor som gäller för deltagandet.",
      ],
    },
    infoPadel: {
      title: "Padel- och tennisprofilen",
      body: [
        "Padel- och tennisprofilen är för elever som vill kombinera skolan med regelbunden träning på banor i närområdet, med tränare och upplägg anpassat efter nivå och ålder.",
        "Träningen sker i små grupper om max fyra elever, med egen tränare och tillgång till omklädningsrum och duschar.",
        "Kryssa i rutan så kontaktar vi er med schema, träningsupplägg och bekräftad kostnad innan något bokas.",
      ],
    },


    addAnotherStudent: "+ Lägg till ytterligare en elev",

    guardianSectionTitleSingle: "3. Vårdnadshavare",
    guardianSectionTitleMulti: "Vårdnadshavare",
    guardianSectionDescription:
      "Vårdnadshavarna gäller för alla elever i ansökan. Vi kontaktar er på e-post och telefon.",
    guardianLabel: (n: number) => `Vårdnadshavare ${n}`,
    remove: "Ta bort",
    email: "E-post",
    phone: "Telefon",
    relationToStudent: "Relation till eleven",
    chooseRelation: "Välj relation",
    guardianNationalityHint: "Nationaliteten avgör vilken ID-handling som gäller.",
    guardianMissingIdCheckbox: (label: string) => `Saknar ${label}`,
    addressForGuardian: "Adress",
    sameAddressAsStudentN: (name: string) => `Samma adress som ${name}`,
    studentFallback: (n: number) => `elev ${n}`,
    feeStudentFallback: (n: number) => `Elev ${n}`,
    ownAddress: "Egen adress",
    sameAddressAsStudent: "Samma adress som eleven",
    sameAddressAsStudentHint: "Kryssa ur om vårdnadshavaren bor på en annan adress än eleven.",
    addGuardian: "Lägg till vårdnadshavare",

    feeSectionTitle: "Beräknad avgift",

    relocationSectionTitle: "Genomgång inför flytten",
    relocationCheckbox: "Ja, vi vill ha genomgången inför flytten (45 min, ingår vid heltidsplats).",
    relocationHint: "Tidigast 15 dagar efter påskrivet avtal och genomförd betalning, på plats eller digitalt.",
    relocationFullTimeOnly: "Genomgången gäller vid heltidsplats – välj heltid för barnet ovan för att kunna boka in den.",
    relocationReadMore: "Läs vad vi går igenom",
    relocationInfo: {
      title: "Genomgång inför flytten",
      body: [
        "En personlig genomgång på 45 minuter, en gång per familj, för er som anmäler minst ett barn på heltid en eller flera terminer (inte vid korttidsplats).",
        "Vi går igenom praktiska råd om flytten, skolgången och vardagen i Spanien. Den sker tidigast 15 dagar efter att avtalet är påskrivet och betalningen är genomförd, på plats eller digitalt – när det passar er.",
      ],
      disclaimer: "Genomgången är allmän vägledning från skolan och ersätter inte juridisk eller ekonomisk rådgivning.",
    },
    relocationFullPage: "Läs allt om genomgången",
    openInNewTab: "Öppna i ny flik",

    consentSectionTitle: "Godkännande och inskick",
    consentPrefix: "Jag har tagit del av integritetspolicyn och användarvillkoren och bekräftar att uppgifterna är korrekta och att jag har rätt att lämna dem.",
    healthConsent: "Jag bekräftar att uppgifterna om hälsa, allergier, medicin och behov av särskilt stöd är fullständiga, att jag meddelar skolan utan dröjsmål om något tillkommer eller förändras, och att skolan behandlar dem för att kunna ta hand om mitt barn på ett säkert sätt och planera rätt stöd.",
    healthConsentShort: "Bekräftelse om hälsouppgifter",
    privacyPolicy: "integritetspolicyn",
    and: "och",
    terms: "användarvillkoren",
    submit: "Skicka ansökan",
    submitting: "Skickar…",
    autoSaveNote: "Uppgifterna sparas medan du fyller i, så du kan fortsätta senare.",
    notBindingNote: "Anmälan är inte bindande. Nästa steg är ett besök eller ett samtal.",

    feeSummaryTitle: "Beräknad avgift",
    subtotal: "Delsumma",
    dueBy: (date: string) => `Betalas senast: ${date}`,
    total: "Beräknad avgift",
    feeShow: "Visa",
    feeHide: "Dölj",
    familyDiscountNote: "Familjerabatt: 20 % på tredje barnet och 50 % på fjärde barnet.",
    feeDisclaimer:
      "Beloppet är en preliminär beräkning utifrån dina val. Skolan bekräftar slutlig avgift och betalningsplan i avtalet.",
    feePendingTitle: "Avgiften är inte fastställd ännu",
    feePendingBody:
      "Avgifterna för den här terminen är inte beslutade ännu. Skicka in ansökan ändå – vi hör av oss med besked om plats och pris.",
    feePendingForTerm: (student: string, term: string) =>
      `${student}: avgiften för ${term} är inte beslutad ännu. Ansökan går att skicka in – vi hör av oss med besked om plats och pris.`,
    feeLoadFailedTitle: "Avgifterna kunde inte laddas just nu",
    feeLoadFailedBody:
      "Vi når inte prislistan för tillfället. Ansökan går att skicka in – vi hör av oss med besked om plats och pris.",
    feeInfoRegistration: {
      title: "Inskrivningsavgift",
      body: [
        "Inskrivningsavgiften är ett engångsbelopp som betalas när eleven skrivs in på skolan. Den säkrar elevens plats och täcker administrationen i samband med inskrivningen.",
        "Avgiften tas bara ut en gång per elev, oavsett hur många terminer eleven går hos oss.",
      ],
    },
    feeInfoRegistrationShort: {
      title: "Inskrivningsavgift korttid",
      body: [
        "Vid en kortare vistelse (vecka eller månad) tas en engångsavgift ut när eleven skrivs in. Beloppet är skolans korttidsavgift och täcker administrationen kring platsen, schemat och gruppen.",
        "Avgiften gäller den valda korttidsperioden och beloppet ovan är det som gäller för perioden och terminen ni valt.",
      ],
    },
    feeInfoLate: {
      title: "Sen ansökningsavgift",
      body: [
        "När en ansökan kommer in nära elevens valda startdatum tillkommer en sen ansökningsavgift.",
        "Avgiften täcker det extra arbete det innebär att ordna plats, schema, grupper och material på kort tid. Ansöker ni i god tid före det valda startdatumet tillkommer ingen sen ansökningsavgift.",
      ],
    },
    progressLabel: (pct: number) => `Ansökan ${pct} % ifylld`,
    progressNext: (label: string) => `Nästa: ${label}`,
    progressDone: "Allt ifyllt – klart att skicka",
    progressShowFee: "Visa beräknad avgift",
    progressHideFee: "Dölj beräknad avgift",
    fillBirthAndStart: "Fyll i födelsedatum och önskad start så räknar vi fram avgiften här.",
    seeFullPriceList: "Se hela prislistan på",
    feesPage: "avgiftssidan",

    doneTitle: "Tack för din ansökan",
    doneBody: (names: string) =>
      `Vi har tagit emot ansökan för ${names}. Skolan hör av sig för besök, nästa steg och besked om plats och avtal – ni behöver inte boka något själva. Har du frågor under tiden når du rektor på`,
    doneNext: "Vad händer nu? Vi granskar er ansökan och hör av oss inom de närmaste dagarna med besked om nästa steg.",
    doneAnother: "Gör en till ansökan",
    doneAnotherHint: "Adress och vårdnadshavare följer med – du fyller bara i det nya barnet.",

    // toasts / validation
    toastCheckFields: "Kontrollera de markerade fälten.",
    toastSuccess: "Tack! Din ansökan är mottagen.",
    toastGenericError: "Något gick fel. Försök igen eller mejla rektor@sweintschool.com.",
    toastFillBirthDate: (n: number) => `Fyll i födelsedatum för elev ${n}.`,
    toastChooseNationality: (n: number) => `Välj nationalitet för elev ${n}.`,
    toastChooseGrade: (n: number) => `Välj årskurs eller nivå för elev ${n}.`,
    toastEnterIdOrCheck: (label: string, n: number) =>
      `Ange ${label.toLowerCase()} för elev ${n}, eller kryssa i att eleven saknar ID-nummer.`,
    toastChooseShortDates: (n: number) => `Välj från- och till-datum för korttiden för elev ${n}.`,
    toastEndAfterStart: (n: number) => `Slutdatum måste vara efter startdatum för elev ${n}.`,
    toastPostalError: (n: number, error: string) => `Elev ${n}: ${error}`,
    toastGuardianNationality: (n: number) => `Välj nationalitet för vårdnadshavare ${n}.`,
    toastGuardianPhone: (n: number) =>
      `Fyll i ett giltigt telefonnummer med landskod för vårdnadshavare ${n}.`,
    toastGuardianId: (n: number, label: string) =>
      `Vårdnadshavare ${n}: ange ${label.toLowerCase()}, eller kryssa i att ID-nummer saknas.`,
    toastGuardianIdInvalid: (n: number, error: string) => `Vårdnadshavare ${n}: ${error}`,
    invalidId: "Ogiltigt ID-nummer",

    passportNumber: "Passnummer",
    passportNumberHint: "Numret står på passets uppslag med foto.",
    passportCountry: "Land som utfärdat passet",
    passportCountryPlaceholder: "Välj land",
    passportIntro:
      "Eftersom ID-nummer saknas behöver vi passuppgifter för att kunna identifiera personen.",
    toastPassport: (n: number) => `Elev ${n}: ange passnummer och vilket land passet är utfärdat i.`,
    toastGuardianPassport: (n: number) =>
      `Vårdnadshavare ${n}: ange passnummer och vilket land passet är utfärdat i.`,

    confirmRemoveStudentTitle: "Ta bort eleven?",
    confirmRemoveStudentBody:
      "Är du helt säker? All information du fyllt i för den här eleven försvinner och går inte att få tillbaka.",
    confirmRemoveGuardianTitle: "Ta bort vårdnadshavaren?",
    confirmRemoveGuardianBody:
      "Är du helt säker? All information du fyllt i för den här vårdnadshavaren försvinner och går inte att få tillbaka.",
    cancel: "Avbryt",
    confirmRemove: "Ja, ta bort",

    usedAddress: "Adress som används",
    addressNotFilledYet: "Adressen är inte ifylld ännu – fyll i den hos eleven ovan.",

    genderOptions: [
      { value: "flicka", label: "Flicka" },
      { value: "pojke", label: "Pojke" },
      { value: "annat", label: "Annat / vill inte ange" },
    ],
    months: [
      "januari", "februari", "mars", "april", "maj", "juni",
      "juli", "augusti", "september", "oktober", "november", "december",
    ],
  },
  en: {
    fillTestData: "Fill in test data",
    testDataFilled: "Test data filled in.",
    draftRestored: "We restored your saved application.",
    draftBannerTitle: "Application in progress saved",
    draftBannerBody: (time: string) => `Everything you fill in is saved automatically in this browser. Last saved ${time}.`,
    draftSaving: "Saving…",
    draftDiscard: "Clear and start over",
    draftDiscardConfirmTitle: "Clear saved application?",
    draftDiscardConfirmBody: "All the information you have entered will be lost. This cannot be undone.",
    draftDiscarded: "Application cleared.",


    studentSectionTitleSingle: "1. The pupil",
    studentSectionTitleMulti: (n: number) => `Pupil ${n}`,
    studentSectionDescription:
      "We need the pupil's name, ID number and date of birth to register the application.",
    removeStudent: "Remove pupil",
    firstName: "First name",
    lastName: "Last name",
    nationality: "Nationality",
    chooseNationality: "Choose nationality",
    searchCountry: "Search country…",
    nationalityHint: "Nationality determines which type of ID document we ask for.",
    swedishPersonalNumberHint:
      "Enter the full personal number, YYYYMMDD-1234. We add the hyphen and century for you. Coordination numbers also work.",
    studentMissingId: "We'll complete the ID number later during enrolment.",
    guardianMissingId: "We'll complete the ID number during enrolment.",
    coordinationNumberOk: "Coordination number approved",
    personalNumberValid: "The personal number is valid",
    lastDigitsLater: "",
    birthDateFilledAuto: " – date of birth filled in automatically.",
    studentMissingIdCheckbox: (label: string) => `Doesn't have a ${label.toLowerCase()}`,

    birthYear: "Birth year",
    birthMonth: "Month",
    birthDay: "Day",
    year: "Year",
    month: "Month",
    day: "Day",
    gender: "Gender",
    chooseGender: "Choose gender",
    sameAddressAsStudent1: "Same address as pupil 1",
    sameAddressAsStudent1Hint: "Untick if the pupil lives at a different address.",
    addressLabel: "Address",
    sameAsStudent1Short: (name: string) => `Same as ${name}`,
    previousSchool: "Previous school",
    previousSchoolHint: "School name and location, e.g. Sofia Distans, Stockholm",
    notes: "Health, allergies and medication",
    notesHint: "Allergies, medication, diet or anything else that is useful for us to know",
    otherNotes: "Other information",
    otherNotesHint: "Anything else you would like the school to know, such as wishes or practical questions. Please do not include health information here.",
    shareHealthInformation: "I want to provide information about health, allergies or medication",

    schoolingSectionTitleSingle: "2. Schooling",
    schoolingSectionTitleMulti: (n: number) => `Schooling – pupil ${n}`,
    schoolingSectionDescription:
      "The term determines both the grade we suggest and which fee applies.",
    desiredStart: "Desired start",
    chooseTerm: "Choose term",
    termHint: "The autumn term starts in September, the spring term in January.",
    period: "Period",
    choosePeriod: "Choose period",
    periodHint:
      "A full term or full year is most common. Short stays apply to shorter visits and have a lower enrolment fee.",
    suggestPrefix: (label: string, term: string) =>
      `We suggest: ${label} (${term}). You can change the level below if you'd like a different placement.`,
    fillBirthDateForSuggestion: "Fill in the date of birth above so we can work out the right grade and school form.",
    gradeLevel: "Grade / level",
    chooseLevel: "Choose level",
    fillBirthDateAbove: "Fill in the date of birth above",
    gradeHint: "Years 7–9 are studied via Sofia Distans and sixth form via Hermods, with on-site teacher support.",
    shortStartFrom: "Short stay from",
    startDate: "Start date",
    termStartsAuto: "The term starts automatically on the first school day.",
    chooseSchoolDayBetween: (first: string, last: string) =>
      `Choose a school day between ${first} and ${last}. Holidays and public holidays cannot be selected.`,
    schoolConfirmsStartDate: "The school confirms the start date for later school years.",
    earliestStartNotice: (date: string) => `The earliest possible start is ${date}, 15 days after the application.`,
    confirmedBySchool: "Confirmed by the school",
    termStartSuffix: " (start of term)",
    chooseOtherDate: "Another date",
    termStart: "Start of term",
    shortEndDate: "Short stay until",
    shortEndDateHint: "The end date is calculated automatically from the school-year calendar.",
    shortEndDatePending: "Choose a start date first",
    shortWeeksCount: "Number of weeks",
    shortMonthsCount: "Number of months",
    weeksOption: (n: number) => (n === 1 ? "1 week" : `${n} weeks`),
    monthsOption: (n: number) => (n === 1 ? "1 month" : `${n} months`),
    shortStayOutsideTerm:
      "The stay does not fit within the term. Choose fewer weeks/months or an earlier start date.",
    endDateAfterStart: "The end date must be after the start date.",
    shortWeekHint: "Weekly short stays are limited to a maximum of two weeks.",
    shortMonthHint: "Monthly short stays are limited to a maximum of two months.",
    wantsAfterschool: "We are signing up for after-school care",
    wantsAfterschoolHint: "After-school care for pupils from preschool class to year 6.",
    afterschoolDiscountBadge: "Save €250",
    afterschoolDiscountHint: "Save €250 when you add after-school care with your application. Applies to a term or a full year.",
    afterschoolOffer:
      "Right now: €250 discount when you add after-school care with your application. Applies to a term or a full year.",
    passportIssuedIn: (country: string) => `Passport issued in ${country}`,
    passportOtherCountry: "The passport was issued in another country",
    wantsFootballAcademy: "We are signing up for Malmö FF's football academy",
    wantsFootballAcademyHint:
      "Training following Malmö FF's model. Requires a school place with us via distance learning. €1,000 per term – the activity is booked one term at a time, even with a full-year school place.",
wantsPadelTennis: "We are signing up for the padel and tennis programme",

    wantsPadelTennisHint:
      "€1,000 per term. Booked one term at a time, even with a full-year school place.",

    specialNeeds: "Need for special support",
    specialNeedsHint: "For example a diagnosis, extra adjustments or support in school.",
    specialNeedsNote: "Feel free to tell us more about the need",

    learnMore: "Read more",
    closeModal: "Close",
    infoAfterschool: {
      title: "After-school care",
      body: [
        "After-school care covers the time after the school day for pupils from preschool class to year 6. Children stay on site with our staff, with time for play, homework and outdoor activities.",
        "It is booked per term in this application and invoiced together with the school fee. If you only need occasional days, we arrange that directly with the school.",
      ],
    },
    infoFootball: {
      title: "Malmö FF's football academy",
      body: [
        "The academy runs in partnership with Malmö FF and follows the club's training model. Sessions are scheduled around the school day and combined with distance learning through Sofia Distans or Hermods, with teacher support on site.",
        "It is open to pupils in years 7–8 and requires a school place with us. The activity is booked one term at a time, in addition to the school fee.",
        "If your child takes part in Malmö FF's football academy, the school helps with the entire application process and the terms that apply to participation.",
      ],
    },
    infoPadel: {
      title: "Padel and tennis programme",
      body: [
        "The padel and tennis programme is for pupils who want to combine school with regular training on nearby courts, with coaches and a set-up adapted to age and level.",
        "Training takes place in small groups of up to four pupils, with a dedicated coach and access to changing rooms and showers.",
        "Tick the box and we'll contact you with the schedule, training set-up and confirmed cost before anything is booked.",
      ],
    },


    addAnotherStudent: "+ Add another pupil",

    guardianSectionTitleSingle: "3. Guardians",
    guardianSectionTitleMulti: "Guardians",
    guardianSectionDescription:
      "Guardians apply to all pupils in the application. We will contact you by email and phone.",
    guardianLabel: (n: number) => `Guardian ${n}`,
    remove: "Remove",
    email: "Email",
    phone: "Phone",
    relationToStudent: "Relation to the pupil",
    chooseRelation: "Choose relation",
    guardianNationalityHint: "Nationality determines which type of ID document applies.",
    guardianMissingIdCheckbox: (label: string) => `Doesn't have a ${label.toLowerCase()}`,
    addressForGuardian: "Address",
    sameAddressAsStudentN: (name: string) => `Same address as ${name}`,
    studentFallback: (n: number) => `pupil ${n}`,
    feeStudentFallback: (n: number) => `Pupil ${n}`,
    ownAddress: "Own address",
    sameAddressAsStudent: "Same address as the pupil",
    sameAddressAsStudentHint: "Untick if the guardian lives at a different address to the pupil.",
    addGuardian: "Add guardian",

    feeSectionTitle: "Estimated fee",

    relocationSectionTitle: "Relocation consultation",
    relocationCheckbox: "Yes, we'd like the relocation consultation (45 min, included with a full-time place).",
    relocationHint: "At the earliest 15 days after the signed contract and completed payment, on-site or online.",
    relocationFullTimeOnly: "The consultation applies to full-time places – choose full-time for the child above to book it.",
    relocationReadMore: "Read what we cover",
    relocationInfo: {
      title: "Relocation consultation",
      body: [
        "A personal 45-minute briefing, once per family, for those enrolling at least one child full-time for one or more terms (not for short stays).",
        "We go through practical advice on the move, schooling and everyday life in Spain. It takes place at the earliest 15 days after the contract has been signed and payment has been completed, on-site or online – whenever suits you.",
      ],
      disclaimer: "The consultation is general guidance from the school and does not replace legal or financial advice.",
    },
    relocationFullPage: "Read everything about the consultation",
    openInNewTab: "Open in a new tab",

    consentSectionTitle: "Consent and submission",
    consentPrefix: "I have read the privacy policy and terms of use and confirm that the information is correct and that I have the right to provide it.",
    healthConsent: "I confirm that the information about health, allergies, medication and the need for special support is complete, that I will notify the school without delay if anything is added or changes, and that the school processes it to care for my child safely and plan the right support.",
    healthConsentShort: "Health information confirmation",
    privacyPolicy: "privacy policy",
    and: "and",
    terms: "terms of use",
    submit: "Submit application",
    submitting: "Sending…",
    autoSaveNote: "Your details are saved as you go, so you can continue later.",
    notBindingNote: "The application is not binding. The next step is a visit or a conversation.",

    feeSummaryTitle: "Estimated fee",
    subtotal: "Subtotal",
    dueBy: (date: string) => `Due by: ${date}`,
    total: "Estimated fee",
    feeShow: "Show",
    feeHide: "Hide",
    familyDiscountNote: "Family discount: 20% on the third child and 50% on the fourth child.",
    feeDisclaimer:
      "This amount is a preliminary estimate based on your choices. The school will confirm the final fee and payment plan in the contract.",
    feePendingTitle: "The fee is not set yet",
    feePendingBody:
      "Fees for this term have not been decided yet. Send the application anyway – we will get back to you about a place and the price.",
    feePendingForTerm: (student: string, term: string) =>
      `${student}: the fee for ${term} has not been decided yet. You can still send the application – we will get back to you about a place and the price.`,
    feeLoadFailedTitle: "Fees could not be loaded right now",
    feeLoadFailedBody:
      "We cannot reach the price list at the moment. You can still send the application – we will get back to you about a place and the price.",
    feeInfoRegistration: {
      title: "Enrolment fee",
      body: [
        "The enrolment fee is a one-off amount paid when the pupil is enrolled. It secures the pupil's place and covers the administration involved in enrolment.",
        "It is charged only once per pupil, no matter how many terms the pupil stays with us.",
      ],
    },
    feeInfoRegistrationShort: {
      title: "Enrolment fee, short stay",
      body: [
        "For a shorter stay (a week or a month) a one-off fee is charged when the pupil is enrolled. The amount is the school's short-stay enrolment fee and covers the administration around the place, timetable and group.",
        "The fee applies to the short-stay period you selected, and the amount above is the one that applies to that period and term.",
      ],
    },
    feeInfoLate: {
       title: "Late application fee",
      body: [
         "When an application arrives close to the pupil's selected start date, a late application fee applies.",
         "The fee covers the extra work of arranging a place, timetable, groups and materials at short notice. If you apply well before the selected start date, no late application fee is added.",
      ],
    },
    progressLabel: (pct: number) => `Application ${pct}% complete`,
    progressNext: (label: string) => `Next: ${label}`,
    progressDone: "All filled in – ready to send",
    progressShowFee: "Show estimated fee",
    progressHideFee: "Hide estimated fee",
    fillBirthAndStart: "Fill in the date of birth and desired start so we can calculate the fee here.",
    seeFullPriceList: "See the full price list on the",
    feesPage: "fees page",

    doneTitle: "Thank you for your application",
    doneBody: (names: string) =>
      `We have received the application for ${names}. The school will be in touch about a visit, the next steps and the decision about a place and the contract — you do not need to book anything yourself. If you have questions in the meantime, contact the principal at`,
    doneNext: "What happens now? We review your application and get back to you within the next few days with the next steps.",
    doneAnother: "Submit another application",
    doneAnotherHint: "Address and guardians are kept – you only fill in the new child.",

    toastCheckFields: "Please check the highlighted fields.",
    toastSuccess: "Thank you! Your application has been received.",
    toastGenericError: "Something went wrong. Please try again or email rektor@sweintschool.com.",
    toastFillBirthDate: (n: number) => `Please fill in the date of birth for pupil ${n}.`,
    toastChooseNationality: (n: number) => `Please choose a nationality for pupil ${n}.`,
    toastChooseGrade: (n: number) => `Please choose a grade or level for pupil ${n}.`,
    toastEnterIdOrCheck: (label: string, n: number) =>
      `Enter a ${label.toLowerCase()} for pupil ${n}, or tick that the pupil doesn't have an ID number.`,
    toastChooseShortDates: (n: number) => `Choose start and end dates for the short stay for pupil ${n}.`,
    toastEndAfterStart: (n: number) => `The end date must be after the start date for pupil ${n}.`,
    toastPostalError: (n: number, error: string) => `Pupil ${n}: ${error}`,
    toastGuardianNationality: (n: number) => `Please choose a nationality for guardian ${n}.`,
    toastGuardianPhone: (n: number) =>
      `Enter a valid phone number including country code for guardian ${n}.`,
    toastGuardianId: (n: number, label: string) =>
      `Guardian ${n}: enter a ${label.toLowerCase()}, or tick that the ID number is missing.`,
    toastGuardianIdInvalid: (n: number, error: string) => `Guardian ${n}: ${error}`,
    invalidId: "Invalid ID number",

    passportNumber: "Passport number",
    passportNumberHint: "The number is printed on the photo page of the passport.",
    passportCountry: "Country of issue",
    passportCountryPlaceholder: "Choose country",
    passportIntro:
      "As there is no ID number, we need passport details to identify the person.",
    toastPassport: (n: number) =>
      `Pupil ${n}: enter the passport number and the country that issued it.`,
    toastGuardianPassport: (n: number) =>
      `Guardian ${n}: enter the passport number and the country that issued it.`,

    confirmRemoveStudentTitle: "Remove this pupil?",
    confirmRemoveStudentBody:
      "Are you absolutely sure? Everything you have filled in for this pupil will be lost and cannot be recovered.",
    confirmRemoveGuardianTitle: "Remove this guardian?",
    confirmRemoveGuardianBody:
      "Are you absolutely sure? Everything you have filled in for this guardian will be lost and cannot be recovered.",
    cancel: "Cancel",
    confirmRemove: "Yes, remove",

    usedAddress: "Address used",
    addressNotFilledYet: "The address hasn't been filled in yet – add it for the pupil above.",

    genderOptions: [
      { value: "flicka", label: "Girl" },
      { value: "pojke", label: "Boy" },
      { value: "annat", label: "Other / prefer not to say" },
    ],
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
  },
  es: {
    fillTestData: "Rellenar datos de prueba",
    testDataFilled: "Datos de prueba rellenados.",
    draftRestored: "Hemos recuperado tu solicitud guardada.",
    draftBannerTitle: "Solicitud en curso guardada",
    draftBannerBody: (time: string) => `Todo lo que escribas se guarda automáticamente en este navegador. Último guardado: ${time}.`,
    draftSaving: "Guardando…",
    draftDiscard: "Borrar y empezar de nuevo",
    draftDiscardConfirmTitle: "¿Borrar la solicitud guardada?",
    draftDiscardConfirmBody: "Se perderá toda la información introducida. No se puede deshacer.",
    draftDiscarded: "Solicitud borrada.",

    studentSectionTitleSingle: "1. El alumno",
    studentSectionTitleMulti: (n: number) => `Alumno ${n}`,
    studentSectionDescription:
      "Necesitamos el nombre, el número de identificación y la fecha de nacimiento del alumno para registrar la solicitud.",
    removeStudent: "Eliminar alumno",
    firstName: "Nombre",
    lastName: "Apellidos",
    nationality: "Nacionalidad",
    chooseNationality: "Elige la nacionalidad",
    searchCountry: "Buscar país…",
    nationalityHint: "La nacionalidad determina qué documento de identidad solicitamos.",
    swedishPersonalNumberHint:
      "Introduce el número personal completo, AAAAMMDD-1234. Añadimos el guion y el siglo por ti.",
    studentMissingId: "Completaremos el número de identificación más adelante, durante la matrícula.",
    guardianMissingId: "Completaremos el número de identificación durante la matrícula.",
    coordinationNumberOk: "Número de coordinación aceptado",
    personalNumberValid: "El número personal es válido",
    lastDigitsLater: "",
    birthDateFilledAuto: " – fecha de nacimiento rellenada automáticamente.",
    studentMissingIdCheckbox: (label: string) => `No tiene ${label.toLowerCase()}`,

    birthYear: "Año de nacimiento",
    birthMonth: "Mes",
    birthDay: "Día",
    year: "Año",
    month: "Mes",
    day: "Día",
    gender: "Sexo",
    chooseGender: "Elige una opción",
    sameAddressAsStudent1: "Misma dirección que el alumno 1",
    sameAddressAsStudent1Hint: "Desmarca si el alumno vive en otra dirección.",
    addressLabel: "Dirección",
    sameAsStudent1Short: (name: string) => `Igual que ${name}`,
    previousSchool: "Colegio anterior",
    previousSchoolHint: "Nombre y localidad del colegio, por ejemplo Sofia Distans, Estocolmo",
    notes: "Salud, alergias y medicación",
    notesHint: "Alergias, medicación, dieta u otra información útil para nosotros",
    otherNotes: "Otra información",
    otherNotesHint: "Cualquier otra cosa que quieran que el colegio sepa, por ejemplo deseos o cuestiones prácticas. No incluyan aquí información de salud.",
    shareHealthInformation: "Quiero facilitar información sobre salud, alergias o medicación",

    schoolingSectionTitleSingle: "2. Escolarización",
    schoolingSectionTitleMulti: (n: number) => `Escolarización – alumno ${n}`,
    schoolingSectionDescription:
      "El trimestre determina tanto el curso que proponemos como la tarifa aplicable.",
    desiredStart: "Inicio deseado",
    chooseTerm: "Elige el periodo",
    termHint: "El semestre de otoño empieza en septiembre y el de primavera en enero.",
    period: "Periodo",
    choosePeriod: "Elige el periodo",
    periodHint:
      "Lo habitual es un semestre o el curso completo. La estancia corta se aplica a visitas breves y tiene una matrícula más baja.",
    suggestPrefix: (label: string, term: string) =>
      `Nuestra propuesta: ${label} (${term}). Puedes cambiar el nivel más abajo si prefieres otra ubicación.`,
    fillBirthDateForSuggestion: "Introduce la fecha de nacimiento arriba y calcularemos el curso adecuado.",
    gradeLevel: "Curso / nivel",
    chooseLevel: "Elige el nivel",
    fillBirthDateAbove: "Introduce la fecha de nacimiento arriba",
    gradeHint: "Los cursos 7–9 se estudian con Sofia Distans y el bachillerato con Hermods, con apoyo docente presencial.",
    shortStartFrom: "Estancia corta desde",
    startDate: "Fecha de inicio",
    termStartsAuto: "El periodo empieza automáticamente el primer día lectivo.",
    chooseSchoolDayBetween: (first: string, last: string) =>
      `Elige un día lectivo entre ${first} y ${last}. No se pueden seleccionar vacaciones ni festivos.`,
    schoolConfirmsStartDate: "El colegio confirma la fecha de inicio para cursos posteriores.",
    earliestStartNotice: (date: string) => `El inicio más temprano posible es el ${date}, 15 días después de la solicitud.`,
    confirmedBySchool: "Lo confirma el colegio",
    termStartSuffix: " (inicio del periodo)",
    chooseOtherDate: "Otra fecha",
    termStart: "Inicio del periodo",
    shortEndDate: "Estancia corta hasta",
    shortEndDateHint: "La fecha final se calcula automáticamente según el calendario escolar.",
    shortEndDatePending: "Elige primero la fecha de inicio",
    shortWeeksCount: "Número de semanas",
    shortMonthsCount: "Número de meses",
    weeksOption: (n: number) => (n === 1 ? "1 semana" : `${n} semanas`),
    monthsOption: (n: number) => (n === 1 ? "1 mes" : `${n} meses`),
    shortStayOutsideTerm:
      "La estancia no cabe dentro del periodo. Elige menos semanas/meses o una fecha de inicio anterior.",
    endDateAfterStart: "La fecha final debe ser posterior a la de inicio.",
    shortWeekHint: "La estancia corta por semanas admite un máximo de dos semanas.",
    shortMonthHint: "La estancia corta por meses admite un máximo de dos meses.",
    wantsAfterschool: "Nos inscribimos en las actividades extraescolares",
    wantsAfterschoolHint: "Atención después de la jornada para alumnos de preescolar a 6.º curso.",
    afterschoolDiscountBadge: "Ahorra 250 €",
    afterschoolDiscountHint: "Ahorra 250 € si añades el servicio de tarde junto con la solicitud. Válido para un trimestre o el curso completo.",
    afterschoolOffer:
      "Ahora mismo: 250 € de descuento al añadir las actividades extraescolares junto con la solicitud. Válido para un trimestre o el curso completo.",
    passportIssuedIn: (country: string) => `Pasaporte emitido en ${country}`,
    passportOtherCountry: "El pasaporte se emitió en otro país",
    wantsFootballAcademy: "Nos inscribimos en la academia de fútbol del Malmö FF",
    wantsFootballAcademyHint:
      "Entrenamiento según el modelo del Malmö FF. Requiere plaza escolar con nosotros y estudios a distancia. 1 000 € por semestre: la actividad se reserva semestre a semestre, aunque elijas curso completo.",
wantsPadelTennis: "Nos inscribimos en el perfil de pádel y tenis",

    wantsPadelTennisHint:
      "1 000 € por semestre. Se reserva semestre a semestre, aunque elijas curso completo.",

    specialNeeds: "Necesidad de apoyo especial",
    specialNeedsHint: "Por ejemplo un diagnóstico, adaptaciones adicionales o apoyo en el colegio.",
    specialNeedsNote: "Cuéntanos más sobre la necesidad",

    learnMore: "Leer más",
    closeModal: "Cerrar",
    infoAfterschool: {
      title: "Actividades extraescolares",
      body: [
        "Ofrecemos atención después de la jornada escolar para alumnos desde preescolar hasta 6.º curso. Los niños permanecen en el colegio con nuestro personal, con tiempo para jugar, hacer deberes y actividades al aire libre.",
        "Se reserva por semestre en esta solicitud y se factura junto con la cuota escolar. Si solo necesitáis días sueltos, lo organizamos con el colegio.",
      ],
    },
    infoFootball: {
      title: "Academia de fútbol del Malmö FF",
      body: [
        "La academia se desarrolla en colaboración con el Malmö FF y sigue el modelo de entrenamiento del club. Las sesiones se organizan en torno a la jornada escolar y se combinan con estudios a distancia mediante Sofia Distans o Hermods, con apoyo docente presencial.",
        "Está abierta a alumnos de 7.º y 8.º y requiere plaza escolar con nosotros. Se reserva semestre a semestre, además de la cuota escolar.",
        "Si el alumno participa en la academia de fútbol del Malmö FF, el colegio se encarga de todo el proceso de solicitud y de las condiciones de participación.",
      ],
    },
    infoPadel: {
      title: "Perfil de pádel y tenis",
      body: [
        "El perfil de pádel y tenis es para alumnos que quieren combinar el colegio con entrenamiento regular en pistas cercanas, con entrenadores y un programa adaptado a la edad y el nivel.",
        "El entrenamiento se realiza en grupos reducidos de hasta cuatro alumnos, con entrenador propio y acceso a vestuarios y duchas.",
        "Marca la casilla y os contactaremos con el horario, el programa y el coste confirmado antes de reservar nada.",
      ],
    },

    addAnotherStudent: "+ Añadir otro alumno",

    guardianSectionTitleSingle: "3. Tutores legales",
    guardianSectionTitleMulti: "Tutores legales",
    guardianSectionDescription:
      "Los tutores se aplican a todos los alumnos de la solicitud. Os contactaremos por correo electrónico y teléfono.",
    guardianLabel: (n: number) => `Tutor ${n}`,
    remove: "Eliminar",
    email: "Correo electrónico",
    phone: "Teléfono",
    relationToStudent: "Relación con el alumno",
    chooseRelation: "Elige la relación",
    guardianNationalityHint: "La nacionalidad determina qué documento de identidad se aplica.",
    guardianMissingIdCheckbox: (label: string) => `No tiene ${label.toLowerCase()}`,
    addressForGuardian: "Dirección",
    sameAddressAsStudentN: (name: string) => `Misma dirección que ${name}`,
    studentFallback: (n: number) => `alumno ${n}`,
    feeStudentFallback: (n: number) => `Alumno ${n}`,
    ownAddress: "Dirección propia",
    sameAddressAsStudent: "Misma dirección que el alumno",
    sameAddressAsStudentHint: "Desmarca si el tutor vive en una dirección distinta a la del alumno.",
    addGuardian: "Añadir tutor",

    feeSectionTitle: "Tarifa estimada",

    relocationSectionTitle: "Asesoría de reubicación",
    relocationCheckbox: "Sí, queremos la asesoría antes de la mudanza (45 min, incluida con plaza a jornada completa).",
    relocationHint: "Como pronto 15 días después de firmar el contrato y completar el pago, presencial o en línea.",
    relocationFullTimeOnly: "La asesoría se aplica a plazas a jornada completa – elige jornada completa para el alumno arriba para poder reservarla.",
    relocationReadMore: "Ver qué incluye",
    relocationInfo: {
      title: "Asesoría de reubicación",
      body: [
        "Una sesión personal de 45 minutos, una vez por familia, para quienes inscriben al menos a un alumno a jornada completa durante uno o más trimestres (no en estancias cortas).",
        "Repasamos consejos prácticos sobre la mudanza, la escolarización y la vida cotidiana en España. Se realiza como pronto 15 días después de firmar el contrato y completar el pago, presencial o en línea, cuando os convenga.",
      ],
      disclaimer: "La asesoría es una orientación general del colegio y no sustituye el asesoramiento jurídico o financiero.",
    },
    relocationFullPage: "Leer todo sobre la asesoría",
    openInNewTab: "Abrir en una pestaña nueva",

    consentSectionTitle: "Consentimiento y envío",
    consentPrefix: "He leído la política de privacidad y las condiciones de uso y confirmo que los datos son correctos y que tengo derecho a facilitarlos.",
    healthConsent: "Confirmo que la información sobre salud, alergias, medicación y necesidades de apoyo especial es completa, que informaré al colegio sin demora si algo se añade o cambia, y que el colegio la trata para cuidar de mi hijo o hija de forma segura y planificar el apoyo adecuado.",
    healthConsentShort: "Confirmación de información de salud",
    privacyPolicy: "política de privacidad",
    and: "y",
    terms: "las condiciones de uso",
    submit: "Enviar solicitud",
    submitting: "Enviando…",
    autoSaveNote: "Los datos se guardan mientras rellenas, así puedes continuar más tarde.",
    notBindingNote: "La solicitud no es vinculante. El siguiente paso es una visita o una conversación.",

    feeSummaryTitle: "Tarifa estimada",
    subtotal: "Subtotal",
    dueBy: (date: string) => `Fecha límite de pago: ${date}`,
    total: "Tarifa estimada",
    feeShow: "Mostrar",
    feeHide: "Ocultar",
    familyDiscountNote: "Descuento familiar: 20 % en el tercer hijo y 50 % en el cuarto.",
    feeDisclaimer:
      "El importe es una estimación preliminar según tus elecciones. El colegio confirma la tarifa final y el plan de pago en el contrato.",
    feePendingTitle: "La tarifa aún no está fijada",
    feePendingBody:
      "Las tarifas de este trimestre todavía no están decididas. Envía la solicitud igualmente: te informaremos sobre la plaza y el precio.",
    feePendingForTerm: (student: string, term: string) =>
      `${student}: la tarifa de ${term} todavía no está decidida. Puedes enviar la solicitud igualmente: te informaremos sobre la plaza y el precio.`,
    feeLoadFailedTitle: "No se pudieron cargar las tarifas",
    feeLoadFailedBody:
      "Ahora mismo no podemos acceder a la lista de precios. Puedes enviar la solicitud igualmente: te informaremos sobre la plaza y el precio.",
    feeInfoRegistration: {
      title: "Cuota de inscripción",
      body: [
        "La cuota de inscripción es un importe único que se abona cuando el alumno se matricula. Garantiza la plaza del alumno y cubre la gestión administrativa de la matrícula.",
        "Solo se cobra una vez por alumno, independientemente de los trimestres que permanezca con nosotros.",
      ],
    },
    feeInfoRegistrationShort: {
      title: "Cuota de inscripción para estancia corta",
      body: [
        "En una estancia corta (una semana o un mes) se cobra un importe único al matricular al alumno. Es la cuota de estancia corta del colegio y cubre la gestión de la plaza, el horario y el grupo.",
        "La cuota corresponde al periodo corto elegido, y el importe indicado arriba es el que se aplica a ese periodo y trimestre.",
      ],
    },
    feeInfoLate: {
      title: "Tasa por solicitud tardía",
      body: [
        "Cuando una solicitud llega cerca de la fecha de inicio elegida para el alumno, se aplica una tasa por solicitud tardía.",
        "La tasa cubre el trabajo extra de organizar plaza, horario, grupos y material con poco tiempo. Si solicitáis con antelación suficiente antes de la fecha elegida, no se aplica ninguna tasa por solicitud tardía.",
      ],
    },
    progressLabel: (pct: number) => `Solicitud ${pct} % completada`,
    progressNext: (label: string) => `Siguiente: ${label}`,
    progressDone: "Todo completado: listo para enviar",
    progressShowFee: "Mostrar tarifa estimada",
    progressHideFee: "Ocultar tarifa estimada",
    fillBirthAndStart: "Introduce la fecha de nacimiento y el inicio deseado y calcularemos la tarifa aquí.",
    seeFullPriceList: "Consulta la lista completa de precios en la",
    feesPage: "página de tarifas",

    doneTitle: "Gracias por tu solicitud",
    doneBody: (names: string) =>
      `Hemos recibido la solicitud de ${names}. El colegio se pondrá en contacto para la visita, los siguientes pasos y la respuesta sobre la plaza y el contrato: no tenéis que reservar nada por vuestra cuenta. Si tienes preguntas mientras tanto, escribe a la dirección`,
    doneNext: "¿Qué pasa ahora? Revisaremos la solicitud y le responderemos en los próximos días con los siguientes pasos.",
    doneAnother: "Enviar otra solicitud",
    doneAnotherHint: "La dirección y los tutores se mantienen: solo hay que rellenar los datos del nuevo alumno.",

    toastCheckFields: "Revisa los campos marcados.",
    toastSuccess: "¡Gracias! Hemos recibido tu solicitud.",
    toastGenericError: "Algo ha salido mal. Inténtalo de nuevo o escribe a rektor@sweintschool.com.",
    toastFillBirthDate: (n: number) => `Introduce la fecha de nacimiento del alumno ${n}.`,
    toastChooseNationality: (n: number) => `Elige la nacionalidad del alumno ${n}.`,
    toastChooseGrade: (n: number) => `Elige el curso o nivel del alumno ${n}.`,
    toastEnterIdOrCheck: (label: string, n: number) =>
      `Introduce ${label.toLowerCase()} para el alumno ${n}, o marca que no dispone de número de identificación.`,
    toastChooseShortDates: (n: number) => `Elige las fechas de inicio y fin de la estancia corta del alumno ${n}.`,
    toastEndAfterStart: (n: number) => `La fecha final debe ser posterior a la de inicio para el alumno ${n}.`,
    toastPostalError: (n: number, error: string) => `Alumno ${n}: ${error}`,
    toastGuardianNationality: (n: number) => `Elige la nacionalidad del tutor ${n}.`,
    toastGuardianPhone: (n: number) =>
      `Escribe un teléfono válido con prefijo de país para el tutor ${n}.`,
    toastGuardianId: (n: number, label: string) =>
      `Tutor ${n}: introduce ${label.toLowerCase()}, o marca que no dispone de número de identificación.`,
    toastGuardianIdInvalid: (n: number, error: string) => `Tutor ${n}: ${error}`,
    invalidId: "Número de identificación no válido",

    passportNumber: "Número de pasaporte",
    passportNumberHint: "El número aparece en la página del pasaporte con la fotografía.",
    passportCountry: "País emisor del pasaporte",
    passportCountryPlaceholder: "Elige el país",
    passportIntro:
      "Como no hay número de identificación, necesitamos los datos del pasaporte para identificar a la persona.",
    toastPassport: (n: number) =>
      `Alumno ${n}: introduce el número de pasaporte y el país emisor.`,
    toastGuardianPassport: (n: number) =>
      `Tutor ${n}: introduce el número de pasaporte y el país emisor.`,

    confirmRemoveStudentTitle: "¿Eliminar al alumno?",
    confirmRemoveStudentBody:
      "¿Seguro que quieres continuar? Se perderá toda la información introducida para este alumno y no se podrá recuperar.",
    confirmRemoveGuardianTitle: "¿Eliminar al tutor?",
    confirmRemoveGuardianBody:
      "¿Seguro que quieres continuar? Se perderá toda la información introducida para este tutor y no se podrá recuperar.",
    cancel: "Cancelar",
    confirmRemove: "Sí, eliminar",

    usedAddress: "Dirección utilizada",
    addressNotFilledYet: "Todavía no se ha indicado la dirección: complétala en los datos del alumno.",

    genderOptions: [
      { value: "flicka", label: "Niña" },
      { value: "pojke", label: "Niño" },
      { value: "annat", label: "Otro / prefiero no decirlo" },
    ],
    months: [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
    ],
  },
} as const satisfies Record<EnrollmentLocale, Record<string, unknown>>;

/** Engelska etiketter för de svenska värden som lagras oförändrade i formuläret. */
export const RELATION_LABEL_EN: Record<string, string> = {
  "Mamma": "Mother",
  "Pappa": "Father",
  "Vårdnadshavare": "Guardian",
  "Mor-/farförälder": "Grandparent",
  "Annan anhörig": "Other relative",
};

export const TERM_LABEL_EN: Record<TermId, string> = {
  HT26: "Autumn term 2026",
  VT27: "Spring term 2027",
  HT27: "Autumn term 2027",
  VT28: "Spring term 2028",
  HT28: "Autumn term 2028",
};

export const PERIOD_LABEL_EN: Record<PeriodId, string> = {
  term: "One term",
  year: "Full year (two terms)",
  short_week: "Short stay 1 week (max 2 weeks)",
  short_month: "Short stay 1 month (max 2 months)",
};

export const GRADE_LABEL_EN: Record<string, string> = {
  forskola: "Preschool (age 3–5)",
  forskoleklass: "Preschool class",
  ak1: "Year 1",
  ak2: "Year 2",
  ak3: "Year 3",
  ak4: "Year 4",
  ak5: "Year 5",
  ak6: "Year 6",
  ak7: "Year 7 (Sofia Distans)",
  ak8: "Year 8 (Sofia Distans)",
  ak9: "Year 9 (Sofia Distans)",
  gy1: "Sixth form year 1 (Hermods)",
  gy2: "Sixth form year 2 (Hermods)",
  gy3: "Sixth form year 3 (Hermods)",
};

/** Engelska landnamn för de vanligaste nationaliteterna, keyed by the Swedish stored value. */
export const NATIONALITY_LABEL_EN: Record<string, string> = {
  "Afghanistan": "Afghanistan",
  "Albanien": "Albania",
  "Algeriet": "Algeria",
  "Argentina": "Argentina",
  "Australien": "Australia",
  "Belgien": "Belgium",
  "Bosnien och Hercegovina": "Bosnia and Herzegovina",
  "Brasilien": "Brazil",
  "Bulgarien": "Bulgaria",
  "Kanada": "Canada",
  "Chile": "Chile",
  "Colombia": "Colombia",
  "Costa Rica": "Costa Rica",
  "Cypern": "Cyprus",
  "Danmark": "Denmark",
  "Egypten": "Egypt",
  "Estland": "Estonia",
  "Finland": "Finland",
  "Frankrike": "France",
  "Grekland": "Greece",
  "Indien": "India",
  "Indonesien": "Indonesia",
  "Irak": "Iraq",
  "Iran": "Iran",
  "Irland": "Ireland",
  "Island": "Iceland",
  "Israel": "Israel",
  "Italien": "Italy",
  "Japan": "Japan",
  "Kina": "China",
  "Kroatien": "Croatia",
  "Lettland": "Latvia",
  "Libanon": "Lebanon",
  "Litauen": "Lithuania",
  "Luxemburg": "Luxembourg",
  "Marocko": "Morocco",
  "Mexiko": "Mexico",
  "Moldavien": "Moldova",
  "Nederländerna": "Netherlands",
  "Norge": "Norway",
  "Nya Zeeland": "New Zealand",
  "Pakistan": "Pakistan",
  "Polen": "Poland",
  "Portugal": "Portugal",
  "Rumänien": "Romania",
  "Ryssland": "Russia",
  "Schweiz": "Switzerland",
  "Serbien": "Serbia",
  "Slovakien": "Slovakia",
  "Slovenien": "Slovenia",
  "Spanien": "Spain",
  "Storbritannien": "United Kingdom",
  "Sudan": "Sudan",
  "Sverige": "Sweden",
  "Sydafrika": "South Africa",
  "Sydkorea": "South Korea",
  "Syrien": "Syria",
  "Tjeckien": "Czechia",
  "Turkiet": "Türkiye",
  "Tyskland": "Germany",
  "Ukraina": "Ukraine",
  "Ungern": "Hungary",
  "Uruguay": "Uruguay",
  "USA": "United States",
  "Venezuela": "Venezuela",
  "Vietnam": "Vietnam",
  "Österrike": "Austria",
};

export const NATIONALITY_GROUP_LABEL_EN: Record<string, string> = {
  "Vanligast": "Most common",
  "Alla länder A–Ö": "All countries A–Z",
};

/** Spanska etiketter för de svenska värden som lagras oförändrade i formuläret. */
export const RELATION_LABEL_ES: Record<string, string> = {
  "Mamma": "Madre",
  "Pappa": "Padre",
  "Vårdnadshavare": "Tutor legal",
  "Mor-/farförälder": "Abuelo/abuela",
  "Annan anhörig": "Otro familiar",
};

export const TERM_LABEL_ES: Record<TermId, string> = {
  HT26: "Semestre de otoño 2026",
  VT27: "Semestre de primavera 2027",
  HT27: "Semestre de otoño 2027",
  VT28: "Semestre de primavera 2028",
  HT28: "Semestre de otoño 2028",
};

export const PERIOD_LABEL_ES: Record<PeriodId, string> = {
  term: "Un semestre",
  year: "Curso completo (dos semestres)",
  short_week: "Estancia corta 1 semana (máx. 2 semanas)",
  short_month: "Estancia corta 1 mes (máx. 2 meses)",
};

export const GRADE_LABEL_ES: Record<string, string> = {
  forskola: "Preescolar (3–5 años)",
  forskoleklass: "Clase preescolar",
  ak1: "1.º curso",
  ak2: "2.º curso",
  ak3: "3.º curso",
  ak4: "4.º curso",
  ak5: "5.º curso",
  ak6: "6.º curso",
  ak7: "7.º curso (Sofia Distans)",
  ak8: "8.º curso (Sofia Distans)",
  ak9: "9.º curso (Sofia Distans)",
  gy1: "Bachillerato 1 (Hermods)",
  gy2: "Bachillerato 2 (Hermods)",
  gy3: "Bachillerato 3 (Hermods)",
};

/** Spanska landnamn, keyed by the Swedish stored value. */
export const NATIONALITY_LABEL_ES: Record<string, string> = {
  "Afghanistan": "Afganistán",
  "Albanien": "Albania",
  "Algeriet": "Argelia",
  "Argentina": "Argentina",
  "Australien": "Australia",
  "Belgien": "Bélgica",
  "Bosnien och Hercegovina": "Bosnia y Herzegovina",
  "Brasilien": "Brasil",
  "Bulgarien": "Bulgaria",
  "Kanada": "Canadá",
  "Chile": "Chile",
  "Colombia": "Colombia",
  "Costa Rica": "Costa Rica",
  "Cypern": "Chipre",
  "Danmark": "Dinamarca",
  "Egypten": "Egipto",
  "Estland": "Estonia",
  "Finland": "Finlandia",
  "Frankrike": "Francia",
  "Grekland": "Grecia",
  "Indien": "India",
  "Indonesien": "Indonesia",
  "Irak": "Irak",
  "Iran": "Irán",
  "Irland": "Irlanda",
  "Island": "Islandia",
  "Israel": "Israel",
  "Italien": "Italia",
  "Japan": "Japón",
  "Kina": "China",
  "Kroatien": "Croacia",
  "Lettland": "Letonia",
  "Libanon": "Líbano",
  "Litauen": "Lituania",
  "Luxemburg": "Luxemburgo",
  "Marocko": "Marruecos",
  "Mexiko": "México",
  "Moldavien": "Moldavia",
  "Nederländerna": "Países Bajos",
  "Norge": "Noruega",
  "Nya Zeeland": "Nueva Zelanda",
  "Pakistan": "Pakistán",
  "Polen": "Polonia",
  "Portugal": "Portugal",
  "Rumänien": "Rumanía",
  "Ryssland": "Rusia",
  "Schweiz": "Suiza",
  "Serbien": "Serbia",
  "Slovakien": "Eslovaquia",
  "Slovenien": "Eslovenia",
  "Spanien": "España",
  "Storbritannien": "Reino Unido",
  "Sudan": "Sudán",
  "Sverige": "Suecia",
  "Sydafrika": "Sudáfrica",
  "Sydkorea": "Corea del Sur",
  "Syrien": "Siria",
  "Tjeckien": "Chequia",
  "Turkiet": "Turquía",
  "Tyskland": "Alemania",
  "Ukraina": "Ucrania",
  "Ungern": "Hungría",
  "Uruguay": "Uruguay",
  "USA": "Estados Unidos",
  "Venezuela": "Venezuela",
  "Vietnam": "Vietnam",
  "Österrike": "Austria",
};

export const NATIONALITY_GROUP_LABEL_ES: Record<string, string> = {
  "Vanligast": "Más frecuentes",
  "Alla länder A–Ö": "Todos los países A–Z",
};
